/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { UserService } from 'app/core/services/user-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { forceNull } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import * as Core from 'app/core/models/essette-api';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { guid } from 'app/shared/lib/guid';
import { I18N } from 'aurelia-i18n';

interface IClaimMapping extends IApiSettings {
    claim: string,
    isIdentity: boolean,
    path: string
}

export interface ISignOnProviderSettings extends IApiSettings {
    $claimMappings?: IClaimMapping[];
}
@autoinject()
export class SaveOauthUser extends ComponentBase {
    ////****************** Fields and Properties ************************************////
    @bindable.objectNull
    protected signOnProviders: Core.SignOnProvider[] | null = null;

    protected user: Core.User | null = null;
    protected syncedFields: string[] = [];

    protected validateForm = validate;
    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly userService: UserService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////
    protected bind(): void {
        if(!this.user) this.clear();
    }

    protected detached(): void {
        this.clear();
    }

    ////****************** Functions ************************************************////
    /**
     * Load the selected sign-on provider's settings to determine which user fields will be populated
     * by the SSO provider's claim data.
     */
    private async loadSyncedFields(): Promise<void> {
        const signOnProviderId = this.user?.login?.signOnProvider?.id;
        if(!!signOnProviderId) {
            const signOnProviderSettings: ISignOnProviderSettings = await this.loadSignOnProviderSettings(signOnProviderId);
            this.syncedFields = signOnProviderSettings?.$claimMappings?.map(claim => claim.path) ?? [];
        }
    }

    /**
     * Gets the synced field message if the specified user field is synced by the SSO provider.
     * @param fieldPath the path of the user field to check
     * @returns the synced field message or null if the field is not synced
     */
    private getSyncedFieldMessage(fieldPath: string): string | null {
        return this.syncedFields.includes(fieldPath) ? this.i18n.tr('ui:messages.info.ssoSyncedFields') : null;
    }

    public clear(): void {
        this.user = {
            id: null,
            isActive: true,
            code: null,
            name: {
                first: null,
                last: null
            },
            title: null,
            department: null,
            emailAddress: null,
            login: {
                signOnProvider: null
            },
            phoneNumber: {
                primary: null,
                cell: null
            },
            twoFactorAuthentication: {
                optedIn: false
            }
        };
        this.syncedFields = [];
    }

    // Parse user and build new user
    protected buildUser(): Core.User {
        const user = this.user;
        let saveUser: Core.User = {};
        if(!!user) {
            saveUser = {
                id: null,
                isActive: true,
                code: forceNull(user.code),
                name: {
                    first: forceNull(user.name?.first),
                    last: forceNull(user.name?.last)
                },
                title: forceNull(user.title),
                department: forceNull(user.department),
                emailAddress: forceNull(user.emailAddress),
                // Phone Numbers
                phoneNumber: {
                    primary: user.phoneNumber?.primary?.number ? user.phoneNumber.primary : null,
                    cell: user.phoneNumber?.cell?.number ? user.phoneNumber.cell : null
                },
                login: {
                    signOnProvider: getReference(this.user?.login?.signOnProvider)
                }
            };
        }

        return saveUser;
    }

    protected async submit(): Promise<void> {
        let isSuccessful = await this.saveUser();

        if(isSuccessful) {
            this.clear();
            await this.router.navigateToRoute('core.admin.user', { userId: this.user?.id });
        }

    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadSignOnProviderSettings(signOnProviderId: string): Promise<IApiSettings> {
        try {
            this.state.beginLoad();

            const response = await this.userService.loadSignOnProviderSettings(signOnProviderId);
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveUser(): Promise<boolean> {
        if(!this.user) return false;
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.userService.saveUser(this.buildUser());
            this.user.id = response.id;
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.created('core:account.type.oauth.user.$', isSuccessful);
            this.state.endSave();
            if(isSuccessful) { await this.router.navigateToRoute('core.admin.user', { userId: this.user.id }); }
            return isSuccessful;
        }
    }
}
